import { useState, type ReactNode } from 'react';
import { LanguageSwitcher } from '../../i18n/LanguageSwitcher';
import { useI18n } from '../../i18n';

/**
 * Visual frame shared by every fan-side auth page. Mirrors the
 * admin-ui AuthShell pattern (logo + kicker + title + form + footer)
 * but uses the fan SPA's indigo accent + rounded-2xl cards instead
 * of admin's emerald + tighter radii.
 *
 * Logo asset is served from /logo.svg (drop your real logo there to
 * override the placeholder). Auto-falls-back to a text mark if the
 * file fails to load.
 */
interface Props {
  kicker?: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  banner?: ReactNode;
}

export function AuthShell({ kicker, title, subtitle, children, footer, banner }: Props) {
  const { t } = useI18n();
  const [logoFailed, setLogoFailed] = useState(false);

  return (
    <div
      className="relative min-h-screen overflow-hidden px-4 py-8"
      style={{
        background: 'var(--noxor-bg)',
        color: 'var(--noxor-text)',
      }}
    >
      {/* NOXOR ambient — blue + violet brand glow that gently orbits.
          Pure CSS, GPU compositor only. Respects reduced-motion. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div className="nx-fan-auth-blob nx-fan-auth-blob-a" />
        <div className="nx-fan-auth-blob nx-fan-auth-blob-b" />
        {/* Twinkles around the card — staggered delays for natural rhythm. */}
        <div className="nx-fan-twinkle" style={{ top: '14%', left: '12%', width: '4px', height: '4px', animationDelay: '0s' }} />
        <div className="nx-fan-twinkle" style={{ top: '24%', right: '18%', width: '3px', height: '3px', animationDelay: '1.2s' }} />
        <div className="nx-fan-twinkle" style={{ bottom: '22%', left: '20%', width: '2px', height: '2px', animationDelay: '2.4s' }} />
        <div className="nx-fan-twinkle" style={{ bottom: '14%', right: '14%', width: '4px', height: '4px', animationDelay: '0.8s' }} />
      </div>

      {/* Language switcher pinned top-right. */}
      <div className="absolute right-4 top-4 z-10">
        <LanguageSwitcher />
      </div>

      <div className="relative grid min-h-[calc(100vh-4rem)] place-items-center">
        <div
          className="w-full max-w-sm space-y-6 rounded-2xl border p-7 shadow-[0_20px_60px_-20px_rgba(124,70,243,0.45)] backdrop-blur-md sm:p-8"
          style={{
            borderColor: 'var(--noxor-border)',
            background: 'linear-gradient(180deg, rgba(7,19,47,0.88), rgba(4,16,46,0.85))',
          }}
        >
          {/* Brand — square text-down WHITE artwork (transparent PNG
              with white text), no wrapper needed on dark surface. */}
          <div className="flex flex-col items-center gap-2">
            {logoFailed ? (
              <div className="text-2xl font-bold tracking-tight" style={{ color: 'var(--noxor-text)' }}>
                {t('common.brand')}
              </div>
            ) : (
              <img
                src="/logo_text_down_white.png"
                alt={t('common.brand')}
                className="h-[8.28rem] w-auto"
                // LCP candidate on every auth page (Login / Signup /
                // ForgotPassword / ConfirmAccount). Logo is the
                // dominant above-fold element.
                fetchPriority="high"
                decoding="async"
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  if (el.src.endsWith('/logo_text_down_white.png')) {
                    el.src = '/logo_text_down.png';
                  } else if (el.src.endsWith('/logo_text_down.png')) {
                    el.src = '/logo.png';
                  } else {
                    setLogoFailed(true);
                  }
                }}
              />
            )}
            <div
              className="text-[10px] font-medium uppercase tracking-[0.22em]"
              style={{ color: 'var(--noxor-text-muted)' }}
            >
              {t('common.tagline')}
            </div>
          </div>

          {/* Title block */}
          <div className="space-y-1.5 text-center">
            {kicker && (
              <div
                className="text-[11px] font-medium uppercase tracking-[0.22em]"
                style={{ color: 'var(--noxor-blue-electric)' }}
              >
                {kicker}
              </div>
            )}
            <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--noxor-text)' }}>{title}</h1>
            {subtitle && <p className="text-sm leading-relaxed" style={{ color: 'var(--noxor-text-muted)' }}>{subtitle}</p>}
          </div>

          {banner}
          {children}
          {footer}
        </div>
      </div>
    </div>
  );
}
